/**
 * VersionHistory — docking panel showing the commit timeline for the current branch.
 *
 * Each commit displays message, author, timestamp, and truncated hash.
 * Merge commits show a badge with both parent hashes.
 */

import { Badge, Box, Flex, Heading, ScrollArea, Text } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useCommits } from "../../hooks/useVersioning";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortHash(hash: string | null): string {
  if (!hash) return "";
  return hash.slice(0, 8);
}

export function VersionHistory() {
  const { projectId, currentBranch } = useProvenanceContext();
  const { data: commits, isLoading } = useCommits(projectId, currentBranch);

  return (
    <Box p="3" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Flex align="center" justify="between" mb="3">
        <Heading size="3">Version History</Heading>
        <Badge size="1" color="gray">{currentBranch}</Badge>
      </Flex>

      {isLoading && <Text size="2" color="gray">Loading commits...</Text>}

      {!isLoading && (!commits || commits.length === 0) && (
        <Text size="2" color="gray">No commits on this branch yet.</Text>
      )}

      <ScrollArea style={{ flex: 1 }}>
        <Flex direction="column" gap="1">
          {commits?.map((c) => {
            const isMerge = c.parent_ids && c.parent_ids.length > 1;
            return (
              <Box
                key={c.id}
                p="3"
                style={{
                  borderRadius: "var(--radius-2)",
                  border: "1px solid var(--gray-a4)",
                  background: "var(--color-surface)",
                }}
              >
                <Flex direction="column" gap="1">
                  <Flex align="center" gap="2">
                    <Text size="2" weight="medium" style={{ flex: 1 }}>
                      {c.message}
                    </Text>
                    {isMerge && (
                      <Badge size="1" color="purple">merge</Badge>
                    )}
                  </Flex>

                  <Flex align="center" gap="3">
                    <Text size="1" color="gray">{c.author}</Text>
                    <Text size="1" color="gray">{formatDate(c.created_at)}</Text>
                    <Text
                      size="1"
                      style={{
                        fontFamily: "var(--code-font-family, monospace)",
                        color: "var(--gray-9)",
                      }}
                    >
                      {shortHash(c.snapshot_hash)}
                    </Text>
                  </Flex>

                  {isMerge && c.parent_ids && (
                    <Text size="1" color="gray">
                      Parents: {c.parent_ids.map((p) => shortHash(p)).join(" + ")}
                    </Text>
                  )}
                </Flex>
              </Box>
            );
          })}
        </Flex>
      </ScrollArea>
    </Box>
  );
}
